import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

export interface DeviceLive {
  id: string
  kaseya_device_id: string
  name: string
  company_id: string | null
  company_name: string
  group_id: number
  group_name: string
  organization_id: number
  organization_name: string
  is_agent_installed: boolean
  is_mdm_enrolled: boolean
  status: 'online' | 'offline' | 'unknown' | 'warning'
  last_synced_at: string | null
  ticket_count: number
  // Asset fields
  operating_system: string | null
  ip_address: string | null
  uptime: string | null
  logged_in_user: string | null
  cpu_usage_percent: number | null
  memory_usage_percent: number | null
  disk_usage_percent: number | null
  total_disk_space_gb: number | null
  free_disk_space_gb: number | null
  has_asset_data: boolean
  asset_last_synced_at: string | null
}

/**
 * Hook to fetch devices with live Kaseya cache data
 * Uses cached data from kaseya_devices_cache (refreshed every 5 min)
 * Fetches in batches to bypass Supabase 1000 row limit
 */
export const useDevicesLive = () => {
  return useQuery({
    queryKey: ['devicesLive'],
    queryFn: async () => {
      // Fetch all devices from Kaseya cache with pagination
      const allKaseyaDevices: any[] = []
      let offset = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        const { data, error } = await supabase
          .from('kaseya_devices_cache')
          .select('*')
          .order('name')
          .range(offset, offset + pageSize - 1)

        if (error) throw error

        if (data && data.length > 0) {
          allKaseyaDevices.push(...data)
          offset += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      console.log(`Fetched ${allKaseyaDevices.length} devices from cache`)

      // Map companies by kaseya_group_id to get company_id
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name, kaseya_group_id')
        .not('kaseya_group_id', 'is', null)

      const companyMap = new Map(
        companies?.map(c => [c.kaseya_group_id, c]) || []
      )

      // Fetch ticket counts by device name
      const { data: ticketCounts } = await supabase
        .from('tickets')
        .select('device:devices(name)')
        .not('device_id', 'is', null)

      // Build ticket count map by device name
      const ticketCountByName = new Map<string, number>()
      ticketCounts?.forEach((ticket: any) => {
        const deviceName = ticket.device?.name
        if (deviceName) {
          const count = ticketCountByName.get(deviceName) || 0
          ticketCountByName.set(deviceName, count + 1)
        }
      })

      // Merge device data with company info and ticket counts
      const devices: DeviceLive[] = allKaseyaDevices.map(device => {
        const company = companyMap.get(device.group_id)
        const ticketCount = ticketCountByName.get(device.name) || 0

        return {
          id: device.kaseya_device_id,
          kaseya_device_id: device.kaseya_device_id,
          name: device.name,
          company_id: company?.id || null,
          company_name: company?.name || device.organization_name,
          group_id: device.group_id,
          group_name: device.group_name,
          organization_id: device.organization_id,
          organization_name: device.organization_name,
          is_agent_installed: device.is_agent_installed,
          is_mdm_enrolled: device.is_mdm_enrolled,
          status: device.status || (device.is_agent_installed ? 'online' : 'offline'),
          last_synced_at: device.last_synced_at,
          ticket_count: ticketCount,
          // Asset fields
          operating_system: device.operating_system || null,
          ip_address: device.ip_address || null,
          uptime: device.uptime || null,
          logged_in_user: device.logged_in_user || null,
          cpu_usage_percent: device.cpu_usage_percent || null,
          memory_usage_percent: device.memory_usage_percent || null,
          disk_usage_percent: device.disk_usage_percent || null,
          total_disk_space_gb: device.total_disk_space_gb || null,
          free_disk_space_gb: device.free_disk_space_gb || null,
          has_asset_data: device.has_asset_data || false,
          asset_last_synced_at: device.asset_last_synced_at || null,
        }
      })

      return devices
    },
    // Refresh every 60 seconds to pick up cache updates
    refetchInterval: 60000,
    staleTime: 30000,
  })
}
